import { memo, useEffect, useRef, useState } from "react";
import { fileUrl } from "../lib/api";

/**
 * <CreativeDirectorCard/> — frame-asset card for the AI Pro Studio picker.
 *
 * Structure (bottom → top inside the button):
 *   1. Thumbnail <img> positioned at exact percentages of the card:
 *        left 13.76% · top 17.77% · width 73.66% · height 55.52%
 *   2. Frame overlay <img> — fills 100% × 100%, pointer-events: none.
 *      Rest state uses /cds/fs_btn1_1080.webp
 *      Hover/selected state uses /cds/fs_btn2_1080.webp
 *      Both files are preloaded once at the picker screen (see CreativeDirectors.jsx),
 *      so browser cache serves every card off the same two entries.
 *   3. FRESH badge — inside the thumbnail window, top-left corner.
 *   4. Category label — header band (3%–16% of card, inset 10% each side).
 *   5. Style name + description — footer band (75%–95% of card, left 10%,
 *      right ≤ 68% so the gold arrow painted into the artwork is never covered).
 *
 * The card holds a strict 1080/1435 aspect ratio at every screen width. Width
 * comes from the parent grid or carousel; height follows from the ratio.
 *
 * Viewport-only rendering: the thumbnail <img> is only mounted when the card
 * is within ~300 px of the viewport (IntersectionObserver). Cards on other
 * carousel pages hold no decoded bitmap.
 *
 * NO CSS border, box-shadow, outline, gradient, glow, filter, or border-radius
 * is applied to the card itself. Every visual effect lives inside the frame
 * artwork.
 */

const FRAME_REST = "/cds/fs_btn1_1080.webp";
const FRAME_ACTIVE = "/cds/fs_btn2_1080.webp";

// Rendered thumbnails are capped at ~400 CSS px; we don't currently have a
// server-side resize endpoint so browser-native decoding downsamples the
// received bytes into GPU memory near this ceiling. Combined with the
// viewport-only mount below, this keeps mobile memory bounded.
const THUMB_MAX_CSS_WIDTH = 400;

const CreativeDirectorCard = memo(function CreativeDirectorCard({
  director,
  selected = false,
  onSelect,
}) {
  const btnRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const src = director?.thumbnail ? fileUrl(director.thumbnail) : null;
  const posn = director?.preview_image_position || "center";
  const isFresh = !!director?.is_fresh_drop;
  const showActive = hover || selected;

  // IntersectionObserver — mount the thumbnail only when the card is in / near
  // the viewport. rootMargin=300px gives us a comfortable buffer on both axes
  // (matters for horizontal carousels where nothing is off-vertical but a lot
  // is off-horizontal). When the card leaves the buffer we tear the <img>
  // element down so the browser releases its decoded bitmap.
  useEffect(() => {
    const el = btnRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);   // graceful fallback: render as before
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting);
      },
      { root: null, rootMargin: "300px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // If we ever accept object URLs / blob URLs for thumbnails in the future,
  // release them when this card unmounts or when the source flips. For now
  // `fileUrl` returns a plain HTTP URL and there is nothing to revoke — this
  // hook is defensive so future changes can't silently accumulate blobs.
  useEffect(() => {
    if (!src || !src.startsWith("blob:")) return;
    return () => { try { URL.revokeObjectURL(src); } catch (_) { /* noop */ } };
  }, [src]);

  return (
    <button
      ref={btnRef}
      type="button"
      data-testid={`cds-card-${director.slug || director.id}`}
      data-selected={selected ? "true" : "false"}
      aria-pressed={selected}
      aria-label={`Creative Director: ${director.name}${selected ? " (selected)" : ""}`}
      onClick={() => onSelect?.(selected ? null : director)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="relative block w-full overflow-hidden bg-transparent border-0 p-0 m-0 cursor-pointer text-left"
      style={{
        aspectRatio: "1080 / 1435",
        // Explicitly zero out every CSS visual on the card — the frame image
        // owns all decoration.
        borderRadius: 0,
        outline: "none",
        boxShadow: "none",
        filter: "none",
      }}
    >
      {/* ─── 1. Thumbnail (bottom) ─── */}
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          left: "13.76%",
          top: "17.77%",
          width: "73.66%",
          height: "55.52%",
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {inView && src && !imgError && (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: "cover",
              objectPosition: posn,
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 200ms ease-out",
              maxWidth: `${THUMB_MAX_CSS_WIDTH}px`,
              // Fallback: even if maxWidth caps CSS box, keep it filling the window.
              width: "100%",
            }}
          />
        )}
      </div>

      {/* ─── 2. Frame overlay (above thumbnail, below text) ─── */}
      <img
        src={showActive ? FRAME_ACTIVE : FRAME_REST}
        alt=""
        aria-hidden="true"
        draggable={false}
        decoding="async"
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: "none", objectFit: "fill" }}
      />

      {/* ─── 3. FRESH badge (inside the thumbnail window, top-left corner) ─── */}
      {isFresh && (
        <span
          data-testid={`cds-card-fresh-${director.slug || director.id}`}
          className="absolute inline-flex items-center px-1.5 py-[1px] rounded-full text-[8.5px] font-semibold uppercase tracking-[0.14em] leading-none bg-emerald-400/15 text-emerald-200 ring-1 ring-emerald-300/35 backdrop-blur-sm"
          style={{
            // Sits ~4% inside the thumbnail opening (window top=17.77%, left=13.76%)
            top: "20%",
            left: "16.5%",
            pointerEvents: "none",
          }}
        >
          Fresh
        </span>
      )}

      {/* ─── 4. Category label — header band (3%–16% of card) ─── */}
      {director?.category && (
        <div
          className="absolute flex items-center justify-center"
          style={{
            top: "3%",
            left: "10%",
            right: "10%",
            height: "13%",
            pointerEvents: "none",
          }}
        >
          <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-semibold uppercase tracking-[0.18em] leading-none bg-white/6 text-zinc-300 ring-1 ring-white/15 backdrop-blur-sm truncate max-w-full">
            {director.category}
          </span>
        </div>
      )}

      {/* ─── 5. Style name + description — footer band (75%–95% of card) ─── */}
      <div
        className="absolute"
        style={{
          top: "75%",
          left: "10%",
          // Right edge stays clear of the gold arrow (painted into the artwork
          // at the bottom-right). 68% is a hard ceiling per the spec.
          width: "58%",
          maxHeight: "20%",
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <p className={`text-[12.5px] font-bold leading-tight truncate ${selected ? "text-amber-100" : "text-white"}`}>
          {director.name}
        </p>
        {director?.short_description && (
          <p className="mt-0.5 text-[9.5px] leading-snug text-zinc-400 line-clamp-2">
            {director.short_description}
          </p>
        )}
      </div>
    </button>
  );
});

export default CreativeDirectorCard;
